import React, { useMemo } from 'react';
import { LorawanActionTypes } from '@utils/deviceActions';
import {
  DeviceActionProps,
  DeviceActionStepsProps,
} from '@components/Actions/ActionsModal/types';
import { DeviceLoraWanProps } from '@pages/LoraWan/types';
import { useTranslation } from 'react-i18next';
import { InfoCircleOutlined } from '@ant-design/icons';
import {
  Alert,
  Button,
  Form,
  FormInstance,
  Radio,
  Select,
  Tooltip,
} from 'antd';
import useSwr from '@hooks/useSwr';

type LinkMode = 'APPEND' | 'DELETE' | 'REPLACE';

const MAX_CONNECTIONS_PER_DEVICE = 4;

type ConnectingLinkDevicePayloadProps = {
  devEui: string;
  connections: number[];
  linkMode: LinkMode;
};

interface ConnectingLinkPayloadProps {
  action: LorawanActionTypes.CONNECTING_LINK;
  devices: ConnectingLinkDevicePayloadProps[];
}

type DeviceConnectionsProps = {
  id: number;
  description: string;
};

type ConnectingLinkDeviceProps = DeviceLoraWanProps & {
  connections?: number[] | string;
  linkMode?: LinkMode;
};

interface ConnectingLinkProps {
  devices: ConnectingLinkDeviceProps[];
  isUpload?: boolean;
  form: FormInstance<any>;
}

const parseConnections = (connections?: number[] | string) => {
  if (Array.isArray(connections)) {
    return connections
      .map(Number)
      .filter(connection => !Number.isNaN(connection));
  }

  return `${connections ?? ''}`
    .split(';')
    .map(connection => connection.trim())
    .filter(Boolean)
    .map(connection => Number(connection))
    .filter(connection => !Number.isNaN(connection));
};

const useConnectingLink = ({
  devices,
  isUpload,
  form,
}: ConnectingLinkProps): DeviceActionProps => {
  const { t } = useTranslation(['lorawan', 'device-actions']);
  const connections = Form.useWatch('connections', form);
  const linkMode = Form.useWatch('linkMode', form);

  const { data: deviceConnections } = useSwr<DeviceConnectionsProps[]>(
    '/lorawan/connections/simple',
  );

  const hasReachedConnectionsLimit =
    (connections?.length ?? 0) >= MAX_CONNECTIONS_PER_DEVICE;

  const connectionsOptions = useMemo(
    () =>
      Array.isArray(deviceConnections)
        ? deviceConnections?.map(({ id, description }) => ({
            label: description,
            value: id,
            disabled: hasReachedConnectionsLimit && !connections?.includes(id),
          }))
        : [],
    [connections, deviceConnections, hasReachedConnectionsLimit],
  );

  const steps: DeviceActionStepsProps[] = useMemo(
    () => [
      {
        title: t('linkMode'),
        name: 'linkMode',
        initialValue: 'APPEND',
        content: (
          <Radio.Group>
            <Radio.Button value="APPEND">
              {t('linkModeAppend')}
              <Tooltip title={t('linkModeAppendInfo')}>
                <Button
                  type="link"
                  size="small"
                  icon={<InfoCircleOutlined />}
                  onClick={event => event.stopPropagation()}
                />
              </Tooltip>
            </Radio.Button>
            <Radio.Button value="DELETE">
              {t('linkModeDelete')}
              <Tooltip title={t('linkModeDeleteInfo')}>
                <Button
                  type="link"
                  size="small"
                  icon={<InfoCircleOutlined />}
                  onClick={event => event.stopPropagation()}
                />
              </Tooltip>
            </Radio.Button>
            <Radio.Button value="REPLACE">
              {t('linkModeReplace')}
              <Tooltip title={t('linkModeReplaceInfo')}>
                <Button
                  type="link"
                  size="small"
                  icon={<InfoCircleOutlined />}
                  onClick={event => event.stopPropagation()}
                />
              </Tooltip>
            </Radio.Button>
          </Radio.Group>
        ),
      },
      {
        title: t('device-actions:labels.lorawan.lorawan-connecting-link'),
        name: 'connections',
        content: (
          <Select
            mode="multiple"
            placeholder={t('selectUpTo', {
              count: MAX_CONNECTIONS_PER_DEVICE,
            })}
            options={connectionsOptions}
            style={{ width: '100%' }}
          />
        ),
      },
    ],
    [connectionsOptions, t],
  );

  const devicesUploaded = useMemo(
    () =>
      isUpload
        ? devices?.map(device => ({
            devEui: device.devEui,
            connections: parseConnections(device.connections),
            linkMode: device.linkMode ?? 'APPEND',
          }))
        : [],
    [devices, isUpload],
  );

  const hasUploadConnectionsLimitError = useMemo(
    () =>
      isUpload &&
      devicesUploaded?.some(
        device => device.connections.length > MAX_CONNECTIONS_PER_DEVICE,
      ),
    [devicesUploaded, isUpload],
  );

  const payloadToSend = useMemo(
    (): ConnectingLinkPayloadProps => ({
      action: LorawanActionTypes.CONNECTING_LINK,
      devices: isUpload
        ? devicesUploaded
        : devices?.map(device => ({
            devEui: device.devEui,
            connections: parseConnections(connections),
            linkMode: linkMode ?? 'APPEND',
          })),
    }),
    [connections, devices, devicesUploaded, isUpload, linkMode],
  );

  const uploadCustomInfo = useMemo(
    () => (
      <Alert
        type="info"
        message={
          <div>
            <p style={{ margin: 0 }}>
              {`${t('connections')}: `}
              <strong>
                {t('selectUpTo', { count: MAX_CONNECTIONS_PER_DEVICE })}
              </strong>
            </p>
            <p style={{ margin: 0 }}>
              {`APPEND: `}
              <strong>{t('linkModeAppendInfo')}</strong>
            </p>

            <p style={{ margin: 0 }}>
              {`DELETE: `}
              <strong>{t('linkModeDeleteInfo')}</strong>
            </p>

            <p style={{ margin: 0 }}>
              {`REPLACE: `}
              <strong>{t('linkModeReplaceInfo')}</strong>
            </p>
          </div>
        }
      />
    ),
    [t],
  );

  return {
    steps,
    payloadToSend,
    uploadCustomInfo,
    confirmDisabled: hasUploadConnectionsLimitError,
    actionKey: LorawanActionTypes.CONNECTING_LINK,
    title: t('device-actions:labels.lorawan.lorawan-connecting-link'),
  };
};

export default useConnectingLink;
